import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import { enforceMandates, MandateFields } from "@/modules/common/utils/zod";
import { Listing, PaymentTerms } from "@/modules/property-tracks/types";
import { RentalListingSchema } from "@/modules/property-tracks/utils/listing";
import { Button, Typography } from "@mui/material";
import { useState } from "react";
import z from "zod";
import MissingDataModal from "../../common/missing-data-modal";
import EditRentalListingModal from "../rental-listing-forms/edit-rental-listing";
import { usePublishListing } from "@/modules/property-tracks/hooks/listings/publish-listing.hook";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";

type ListingFormFields = z.infer<ReturnType<typeof RentalListingSchema>>;

export default function PublishRentalListing({
  listing,
  trackUid,
}: {
  listing: Listing;
  trackUid: string;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [missingDataModalOpen, setMissingDataModalOpen] = useState(false);

  const mandates: MandateFields<ListingFormFields> = {
    documentUids: true,
    propertyRental: {
      annualIncreasePercent: true,
      availableFrom: true,
      furnished: true,
      insuranceDepositRequired: true,
      minimumStayMonths: true,
      noBrokerCommission: true,
      rentalPrice: true,
      showingFrom: true,
      paymentTerms: true,
    },
  };
  // Build schema instance
  const schema = RentalListingSchema().superRefine((data, ctx) => {
    if (mandates) enforceMandates(data, mandates, ctx, []);
  });

  const mutation = usePublishListing();

  const validate = () => {
    const result = schema.safeParse({
      documentUids: listing.trackDocuments
        ? listing.trackDocuments.map((td) => td.uid)
        : undefined,
      propertyRental: {
        ...listing.propertyRental,
        paymentTerms: listing.propertyRental.paymentTerms
          ? {
              value: listing.propertyRental.paymentTerms,
              label: PaymentTerms[listing.propertyRental.paymentTerms],
            }
          : undefined,
      },
    });
    if (result.success) {
      mutation.mutate(listing.uid, {
        onSuccess: () => {
          handleModalClose();
          showToast({
            type: ToastType.SUCCESS,
            message: "Listing published successfully",
            toastId: TOAST_IDS.PUBLISH_LISTING_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.PUBLISH_LISTING_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      });
    } else {
      // Validation succeeded
      setMissingDataModalOpen(true);
      // handleModalClose();
    }
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const { readonly } = useReadonlyContext();

  return (
    <>
      <Button
        disabled={readonly}
        variant="contained"
        onClick={handleModalOpen}
        sx={(theme) => ({
          display: "flex",
          gap: theme.nawy.spacing.xs,
          alignItems: "center",
          backgroundColor: theme.nawy.colors.semantic.layer.accent,
          textTransform: "none",
          ...theme.nawy.typography.english.variants.body.md.semibold,
        })}
      >
        Publish listing
      </Button>
      <EditFieldModal
        open={modalOpen}
        setOpen={setModalOpen}
        header="Publish listing"
        width={480}
        handleConfirm={validate}
        handleCloseModal={handleModalClose}
        isLoading={mutation.isPending}
        confirmText="Publish"
      >
        <Typography
          sx={(t) => t.nawy.typography.english.variants.body.md.regular}
        >
          Are you sure you want to publish this listing?
        </Typography>
      </EditFieldModal>
      <MissingDataModal
        open={missingDataModalOpen}
        handleOpen={() => setMissingDataModalOpen(true)}
        handleClose={() => {
          setMissingDataModalOpen(false);
        }}
        handleConfirm={() => setEditModalOpen(true)}
      />
      <EditRentalListingModal
        listing={listing}
        open={editModalOpen}
        trackUid={trackUid}
        handleOpen={() => setEditModalOpen(true)}
        handleClose={() => setEditModalOpen(false)}
        manddateFields={mandates}
      />
    </>
  );
}
